import { ReactNode } from "react";
import BottomNav from "./BottomNav";
import FloatingMessageButton from "./FloatingMessageButton";

interface PhoneShellProps {
  children: ReactNode;
  /** Hide the bottom nav (e.g. Share Grace screen has minimal nav) */
  hideNav?: boolean;
}

/**
 * On mobile (<lg) we render full-bleed: real phone, real chrome, no fake frame.
 * On desktop (>=lg) we render the Stitch / Google-style phone mock so the
 * prototype feels intentional inside the wide-screen hero plate.
 */
export default function PhoneShell({ children, hideNav = false }: PhoneShellProps) {
  return (
    <>
      {/* Mobile: full-bleed app, no fake phone frame */}
      <div
        className="lg:hidden min-h-screen w-full relative"
        style={{ background: "var(--mnf-cr)" }}
      >
        <div
          className="relative w-full mnf-scroll"
          style={{
            minHeight: "100vh",
            paddingBottom: hideNav ? 0 : 78,
          }}
        >
          {children}
        </div>
        {!hideNav && <FloatingMessageButton />}
        {!hideNav && <BottomNav />}
      </div>

      {/* Desktop: phone-mock hero plate */}
      <div
        className="hidden lg:flex min-h-screen w-full items-center justify-center relative overflow-hidden"
        style={{
          background:
            "radial-gradient(1200px 600px at 10% -10%, rgba(168,212,184,0.35), transparent 60%)," +
            "radial-gradient(900px 500px at 110% 20%, rgba(192,120,74,0.18), transparent 60%)," +
            "linear-gradient(180deg, #EDE3D2 0%, #F5F0E8 60%, #EFE7D6 100%)",
          padding: "clamp(0px, 4vw, 32px)",
        }}
      >
        {/* Brand watermark */}
        <div
          className="absolute left-12 top-12 flex flex-col gap-1 select-none"
          style={{ color: "var(--mnf-f)" }}
        >
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Move · Nourish · Flourish</p>
          <h1 className="mnf-serif italic text-3xl xl:text-4xl leading-tight" style={{ maxWidth: 360 }}>
            A garden for body, soul, and sisterhood.
          </h1>
          <p className="text-sm mt-2 mnf-body" style={{ color: "var(--mnf-tg)", maxWidth: 320 }}>
            Faith-based fitness, gentle nutrition tools, and a community of sisters tending growth together.
          </p>
        </div>

        <div
          className="absolute right-12 bottom-12 flex flex-col items-end gap-1 select-none"
          style={{ color: "var(--mnf-f)" }}
        >
          <p className="text-xs mnf-body" style={{ color: "var(--mnf-tg)" }}>
            "She is clothed with strength and dignity."
          </p>
          <p className="mnf-eyebrow" style={{ color: "var(--mnf-gold)" }}>Proverbs 31:25</p>
        </div>

        <div
          className="relative w-full"
          style={{
            maxWidth: 412,
            aspectRatio: "412 / 870",
            maxHeight: "calc(100vh - 32px)",
          }}
        >
          {/* Phone shell */}
          <div
            className="absolute inset-0 rounded-[44px] overflow-hidden"
            style={{
              background: "#0a0a0a",
              padding: 8,
              boxShadow:
                "0 30px 80px rgba(30,74,53,0.25), 0 4px 12px rgba(30,74,53,0.10), inset 0 0 0 2px rgba(255,255,255,0.06)",
            }}
          >
            <div
              className="relative w-full h-full overflow-hidden"
              style={{
                background: "var(--mnf-cr)",
                borderRadius: 36,
              }}
            >
              {/* Notch */}
              <div
                className="absolute top-0 left-1/2 -translate-x-1/2 z-50"
                style={{
                  width: 110,
                  height: 26,
                  background: "#000",
                  borderRadius: "0 0 16px 16px",
                }}
              />
              {/* Status bar */}
              <div
                className="relative z-40 flex items-center justify-between px-7 pt-2.5 pb-1"
                style={{ fontSize: 12, fontWeight: 600, color: "var(--mnf-tx)" }}
              >
                <span style={{ fontFamily: "var(--font-sans)" }}>9:41</span>
                <div className="flex items-center gap-1.5" style={{ color: "var(--mnf-tx)" }}>
                  <span className="mnf-icon" style={{ fontSize: 14 }}>signal_cellular_alt</span>
                  <span className="mnf-icon" style={{ fontSize: 14 }}>wifi</span>
                  <span
                    className="mnf-icon filled"
                    style={{ fontSize: 14, fontVariationSettings: "'FILL' 1" }}
                  >
                    battery_full
                  </span>
                </div>
              </div>

              {/* Scrollable content */}
              <div
                className="relative w-full mnf-scroll"
                style={{
                  height: "calc(100% - 30px)",
                  overflowY: "auto",
                  paddingBottom: hideNav ? 0 : 78,
                }}
              >
                {children}
              </div>

              {!hideNav && <FloatingMessageButton />}
              {!hideNav && <BottomNav />}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
